import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Article, TypingHistory } from '../types';

interface TypeMasterDB extends DBSchema {
  articles: {
    key: string;
    value: Article;
  };
  history: {
    key: string;
    value: TypingHistory;
    indexes: {
      'by-article': string;
      'by-date': number;
    };
  };
}

const DB_NAME = 'TypeMasterDB';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<TypeMasterDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<TypeMasterDB>> {
  if (!dbPromise) {
    dbPromise = openDB<TypeMasterDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('articles')) {
          db.createObjectStore('articles', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('history')) {
          const historyStore = db.createObjectStore('history', { keyPath: 'id' });
          historyStore.createIndex('by-article', 'articleId');
          historyStore.createIndex('by-date', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
}

export async function getAllArticles(): Promise<Article[]> {
  const db = await getDB();
  return db.getAll('articles');
}

export async function getArticle(id: string): Promise<Article | undefined> {
  const db = await getDB();
  return db.get('articles', id);
}

export async function saveArticle(article: Article): Promise<void> {
  const db = await getDB();
  await db.put('articles', article);
}

export async function deleteArticle(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('articles', id);
}

export async function getAllHistory(): Promise<TypingHistory[]> {
  const db = await getDB();
  const history = await db.getAll('history');
  return history.sort((a, b) => b.createdAt - a.createdAt);
}

export async function getHistoryByArticle(articleId: string): Promise<TypingHistory[]> {
  const db = await getDB();
  return db.getAllFromIndex('history', 'by-article', articleId);
}

export async function saveHistory(history: TypingHistory): Promise<void> {
  const db = await getDB();
  await db.put('history', history);
}

export async function deleteHistory(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('history', id);
}

export async function clearAllHistory(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('history', 'readwrite');
  await tx.store.clear();
  await tx.done;
}

export const DEFAULT_ARTICLE: Article = {
  id: 'default-happy-birthday',
  title: '生日快乐歌',
  content: `Happy birthday to you
Happy birthday to you
Happy birthday dear friend
Happy birthday to you`,
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

export async function initDefaultArticle(): Promise<void> {
  const db = await getDB();
  const existing = await db.get('articles', DEFAULT_ARTICLE.id);
  if (!existing) {
    await db.put('articles', DEFAULT_ARTICLE);
  }
}
